import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useEscapeClose } from "../../../../utils/SamuiUtils";
import {
  useModalKeyboardNavigation,
  getKeyboardInstructions,
} from "../../../../utils/ModalKeyboardNavigation";
import Pagination from "../../../components/Pagination";

export default function ApprovalQuoteModal({ isOpen, onClose, quotes = [] }) {
  const navigate = useNavigate();
  const [search, setSearch] = useState("");
  const [filteredQuotes, setFilteredQuotes] = useState([]);
  const [selectedRowIndex, setSelectedRowIndex] = useState(0);
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(10);

  useEscapeClose(isOpen, onClose);

  //  Navigation: ลูกศรขึ้นลง + Enter เพื่อเปิด
  useModalKeyboardNavigation(
    isOpen,
    filteredQuotes,
    selectedRowIndex,
    setSelectedRowIndex,
    null,
    onClose,
    {
      customEnterHandler: () => handleApprove(filteredQuotes[selectedRowIndex]),
      enableSpace: false,
    }
  );

  //  กรองข้อมูลจาก search
  useEffect(() => {
    const filtered = quotes.filter(
      (q) =>
        q.Doc_No?.toLowerCase().includes(search.toLowerCase()) ||
        q.AR_Name?.toLowerCase().includes(search.toLowerCase()) ||
        q.Sale_Emp_Name?.toLowerCase().includes(search.toLowerCase())
    );
    setFilteredQuotes(filtered);
    setSelectedRowIndex(0);
    setCurrentPage(1);
  }, [search, quotes]);

  if (!isOpen) return null;

  //  Pagination
  const totalItems = filteredQuotes.length;
  const startIndex = (currentPage - 1) * itemsPerPage;
  const paginatedList = filteredQuotes.slice(startIndex, startIndex + itemsPerPage);

  //  ไปหน้าอนุมัติ
  const handleApprove = (q) => {
    if (!q) return;
    navigate(`/quote/edit/${q.Doc_Id}?AR_Id=${q.AR_Id}&mode=approve&Doc_No=${q.Doc_No}`);
    onClose();
  };

  return (
    <>
      <div
        className={`modal fade ${isOpen ? "show" : ""}`}
        style={{ display: isOpen ? "block" : "none" }}
        tabIndex="-1"
        role="dialog"
      >
        <div className="modal-dialog modal-lg" role="document">
          <div className="modal-content border-0 shadow-lg">
            {/* Header */}
            <div
              className="modal-header"
              style={{ backgroundColor: "#E13F57", color: "white" }}
            >
              <h5 className="modal-title">
                ใบเสนอราคารออนุมัติ ({quotes.length})
              </h5>
              <button type="button" className="close" onClick={onClose}>
                <span>&times;</span>
              </button>
            </div>

            {/* Body */}
            <div className="modal-body">
              {/*  Search bar */}
              <div className="form-group mb-3 d-flex align-items-center justify-between">
                <input
                  type="text"
                  className="form-control w-50"
                  placeholder="ค้นหา เลขที่ / ลูกค้า / พนักงานขาย..."
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                />
              </div>

              {/*  Table */}
              <div className="table-responsive" style={{ maxHeight: "500px" }}>
                <table className="table table-striped table-hover text-sm">
                  <thead style={{ backgroundColor: "#E13F57", color: "white" }}>
                    <tr>
                      <th className="text-center" style={{ width: "10%" }}>
                        เลขที่
                      </th>
                      <th className="text-center" style={{ width: "10%" }}>
                        วันที่
                      </th>
                      <th className="text-left">ลูกค้า</th>
                      <th className="text-left">พนักงานขาย</th>
                      <th className="text-left">สถานที่บริการ</th>
                      <th className="text-center" style={{ width: "10%" }}>
                        สถานะ
                      </th>
                      <th className="text-center" style={{ width: "10%" }}>
                        การอนุมัติ
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {paginatedList.length > 0 ? (
                      paginatedList.map((q, idx) => {
                        const isHighlighted = idx === selectedRowIndex;
                        return (
                          <tr
                            key={q.Doc_Id}
                            onClick={() => handleApprove(q)}
                            style={{
                              cursor: "pointer",
                              backgroundColor: isHighlighted
                                ? "rgba(225,63,87,0.25)"
                                : "transparent",
                              transition: "background-color 0.2s ease",
                            }}
                          >
                            <td className="text-center">{q.Doc_No}</td>
                            <td className="text-center">
                              {new Date(q.Doc_Date).toLocaleDateString("th-TH")}
                            </td>
                            <td className="text-left">{q.AR_Name || "-"}</td>
                            <td className="text-left">{q.Sale_Emp_Name || "-"}</td>
                            <td className="text-left">{q.ServiceAddr_FullText || "-"}</td>
                            <td className="text-center">
                              <span className="bg-yellow-100 text-yellow-700 px-3 py-1 rounded-full text-xs">
                                รออนุมัติ
                              </span>
                            </td>
                            <td className="text-center">
                              <button
                                onClick={(e) => {
                                  e.stopPropagation();
                                  handleApprove(q);
                                }}
                                className="btn btn-sm text-white"
                                style={{
                                  backgroundColor: "#28a745",
                                  borderColor: "#28a745",
                                }}
                              >
                                อนุมัติ
                              </button>
                            </td>
                          </tr>
                        );
                      })
                    ) : (
                      <tr>
                        <td
                          colSpan="7"
                          className="text-center py-4 text-muted"
                        >
                          ไม่พบข้อมูล
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>

              {/*  Pagination */}
              <div className="mt-2 border-top pt-2">
                <Pagination
                  totalItems={filteredQuotes.length}
                  itemsPerPage={itemsPerPage}
                  currentPage={currentPage}
                  onPageChange={setCurrentPage}
                  onItemsPerPageChange={(val) => {
                    setItemsPerPage(val);
                    setCurrentPage(1);
                  }}
                />
              </div>
            </div>

            {/* Footer */}
            <div className="modal-footer">
              <div
                className="text-left text-muted mr-auto"
                style={{ fontSize: "12px" }}
              >
                {getKeyboardInstructions("ใบเสนอราคารออนุมัติ", true)}
              </div>
              <button
                className="btn btn-secondary"
                onClick={onClose}
              >
                ปิด
              </button>
            </div>
          </div>
        </div>
      </div>
      {isOpen && <div className="modal-backdrop fade show"></div>}
    </>
  );
}
